import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, CircleAlert, Copy, Info, LoaderCircle, TriangleAlert } from 'lucide-react';

import type { ApiError } from '../lib/api';
import { shortId } from '../lib/format';
import { useCopy } from '../lib/hooks';
import type { SandboxState, TaskState } from '../lib/types';

export const BILLING_PATH = '/settings/billing';

/* ------------------------------------------------------------ brand */

/** offstage's ramp square and spotlight, the beam landing on a Mac Studio. */
export function Mark({ className = 'mark' }: { className?: string }) {
  const uid = useId().replace(/:/g, '');
  return (
    <svg className={className} viewBox="0 0 128 128" aria-hidden="true">
      <defs>
        <linearGradient id={`ramp-${uid}`} x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#7c3aed" />
          <stop offset="0.5" stopColor="#d946ef" />
          <stop offset="1" stopColor="#f59e0b" />
        </linearGradient>
        <mask id={`ports-${uid}`}>
          <rect width="128" height="128" fill="#fff" />
          <rect x="36" y="92" width="4" height="10" rx="2" fill="#000" />
          <rect x="44" y="92" width="4" height="10" rx="2" fill="#000" />
          <rect x="74" y="96" width="18" height="3" rx="1.5" fill="#000" />
        </mask>
      </defs>
      <rect width="128" height="128" rx="28" fill={`url(#ramp-${uid})`} />
      <path d="M54 16 L74 16 L95 68 L33 68 Z" fill="#fff" fillOpacity="0.92" />
      <rect x="26" y="76" width="76" height="36" rx="8" fill="#fff" fillOpacity="0.92" mask={`url(#ports-${uid})`} />
    </svg>
  );
}

export function Brand({ to = '/' }: { to?: string }) {
  return (
    <Link className="brand" to={to} aria-label="offstage cloud, Sandboxes">
      <Mark />
      <span className="brand__word">offstage</span>
      <span className="brand__sub">cloud</span>
    </Link>
  );
}

/** A Mac Studio front elevation, as on the marketing site's rack. */
export function Studio({ lit = false, waiting = false, className = 'studio' }: { lit?: boolean; waiting?: boolean; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 204 100" aria-hidden="true">
      <rect className="studio__body" x="2" y="2" width="200" height="96" rx="13" />
      <rect className="studio__port" x="26" y="60" width="5" height="14" rx="2.5" />
      <rect className="studio__port" x="38" y="60" width="5" height="14" rx="2.5" />
      <rect className="studio__port" x="140" y="66" width="36" height="4" rx="2" />
      <circle className={`studio__led${waiting ? ' studio__led--wait' : ''}`} cx="188" cy="84" r="3" style={lit ? { fill: 'var(--accent)' } : undefined} />
    </svg>
  );
}

/* ---------------------------------------------------------- pieces */

export function Spinner({ size = 15 }: { size?: number }) {
  return <LoaderCircle className="spin" width={size} height={size} aria-hidden="true" />;
}

export function CopyButton({ text, label = 'Copy', className = 'copy' }: { text: string; label?: string; className?: string }) {
  const { copied, copy } = useCopy();
  return (
    <button
      type="button"
      className={`${className}${copied ? ' is-done' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        void copy(text);
      }}
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
    >
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
    </button>
  );
}

/** A big labelled copy button, for prompts and keys. */
export function CopyAction({ text, label, doneLabel = 'Copied', primary = false, size = '' }: { text: string; label: string; doneLabel?: string; primary?: boolean; size?: '' | 'sm' | 'lg' }) {
  const { copied, copy } = useCopy(2000);
  return (
    <button type="button" className={`btn${primary ? ' btn--primary' : ''}${size ? ` btn--${size}` : ''}`} onClick={() => void copy(text)} aria-live="polite">
      {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      {copied ? doneLabel : label}
    </button>
  );
}

/** A copyable id; links to its detail page when `to` is given. `label` shows a name in its place (a sandbox's), still copying the id. */
export function Id({ id, to, full = false, label }: { id: string; to?: string; full?: boolean; label?: string }) {
  const text = label ?? (full ? id : shortId(id));
  return (
    <span className="id" title={id}>
      {to ? (
        <Link to={to} onClick={(e) => e.stopPropagation()}>
          {text}
        </Link>
      ) : (
        <span>{text}</span>
      )}
      <CopyButton text={id} label={`Copy ${id}`} />
    </span>
  );
}

const TASK_LAMPS: Record<TaskState, { lamp: string; label: string; pill?: string }> = {
  queued: { lamp: 'lamp--hollow', label: 'Queued', pill: 'pill--muted' },
  claimed: { lamp: 'lamp--amber-hollow', label: 'Claimed' },
  running: { lamp: 'lamp--amber lamp--live', label: 'Running' },
  succeeded: { lamp: 'lamp--accent', label: 'Succeeded' },
  failed: { lamp: 'lamp--refused', label: 'Failed', pill: 'pill--failed' },
  timed_out: { lamp: 'lamp--magenta', label: 'Timed out' },
  cancelled: { lamp: 'lamp--off', label: 'Cancelled', pill: 'pill--muted' },
};

const SANDBOX_LAMPS: Record<SandboxState, { lamp: string; label: string; pill?: string }> = {
  pending: { lamp: 'lamp--amber-hollow', label: 'Pending' },
  ready: { lamp: 'lamp--green lamp--live', label: 'Ready', pill: 'pill--ready' },
  closed: { lamp: 'lamp--off', label: 'Closed', pill: 'pill--muted' },
};

export function StatePill({ state }: { state: TaskState | SandboxState }) {
  const spec = (TASK_LAMPS as Record<string, (typeof TASK_LAMPS)[TaskState]>)[state] ?? (SANDBOX_LAMPS as Record<string, (typeof TASK_LAMPS)[TaskState]>)[state];
  if (!spec) return <span className="pill">{state}</span>;
  return (
    <span className={`pill ${spec.pill ?? ''}`}>
      <span className={`lamp ${spec.lamp}`} aria-hidden="true" />
      {spec.label}
    </span>
  );
}

export function Lamp({ tone }: { tone: 'amber' | 'live' | 'accent' | 'off' | 'hollow' | 'magenta' | 'refused' }) {
  const cls = tone === 'live' ? 'lamp--amber lamp--live' : `lamp--${tone}`;
  return <span className={`lamp ${cls}`} aria-hidden="true" />;
}

/* -------------------------------------------------------- callouts */

export function Callout({
  tone = 'info',
  title,
  children,
  actions,
  meta,
  role,
}: {
  tone?: 'info' | 'error' | 'warn' | 'ok';
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  meta?: ReactNode;
  role?: 'alert' | 'status';
}) {
  const Icon = tone === 'error' ? CircleAlert : tone === 'warn' ? TriangleAlert : tone === 'ok' ? Check : Info;
  return (
    <div className={`callout callout--${tone}`} role={role}>
      <Icon aria-hidden="true" />
      <div className="callout__body">
        <p className="callout__title">{title}</p>
        {children ? <div className="callout__text">{children}</div> : null}
        {actions ? <div className="callout__actions">{actions}</div> : null}
        {meta ? <div className="callout__meta">{meta}</div> : null}
      </div>
    </div>
  );
}

const GUIDED: Record<string, { title: string; tone: 'error' | 'warn'; actions: { to: string; label: string; primary?: boolean }[] }> = {
  'payment-method-required': {
    title: 'Add a card to keep going',
    tone: 'warn',
    actions: [{ to: BILLING_PATH, label: 'Add a card', primary: true }],
  },
  'payment-failed': {
    title: 'The last charge failed',
    tone: 'error',
    actions: [{ to: BILLING_PATH, label: 'Update card', primary: true }],
  },
  'budget-exceeded': {
    title: 'This would pass your monthly budget',
    tone: 'warn',
    actions: [
      { to: `${BILLING_PATH}#limit`, label: 'Change limit', primary: true },
      { to: '/usage', label: 'See usage' },
    ],
  },
  'no-capacity': {
    title: 'No Mac in the fleet takes this session right now',
    tone: 'warn',
    actions: [{ to: '/sandboxes', label: 'See capacity', primary: true }],
  },
  forbidden: {
    title: 'Only a team owner can do this',
    tone: 'error',
    actions: [{ to: '/members', label: 'See owners' }],
  },
  'email-unverified': {
    title: 'Verify your email first',
    tone: 'warn',
    actions: [],
  },
  'concurrency-limit': {
    title: 'Your team has every sandbox it may open',
    tone: 'warn',
    actions: [
      { to: '/sandboxes?state=ready', label: 'See open sandboxes', primary: true },
      { to: BILLING_PATH, label: 'Plan limits' },
    ],
  },
};

/** Any API failure, with its message and fix; billing and limit refusals link where to fix them. */
export function ErrorCallout({ error, onRetry, title }: { error: ApiError; onRetry?: () => void; title?: string }) {
  const guided = GUIDED[error.code];
  return (
    <Callout
      tone={guided?.tone ?? 'error'}
      role="alert"
      title={title ?? guided?.title ?? sentence(error.message)}
      meta={
        <>
          {error.status ? `${error.status} ` : ''}
          {error.code}
          {error.requestId ? ` · ${error.requestId}` : ''}
        </>
      }
      actions={
        guided || onRetry ? (
          <>
            {guided?.actions.map((a) => (
              <Link key={a.to + a.label} className={`btn btn--sm${a.primary ? ' btn--primary' : ''}`} to={a.to}>
                {a.label}
              </Link>
            ))}
            {onRetry ? (
              <button type="button" className="btn btn--sm" onClick={onRetry}>
                Try again
              </button>
            ) : null}
          </>
        ) : undefined
      }
    >
      {guided || title ? <p style={{ margin: 0 }}>{sentence(error.message)}</p> : null}
      {error.fix ? <p style={{ margin: guided || title ? '2px 0 0' : 0 }}>{sentence(error.fix)}</p> : null}
    </Callout>
  );
}

/** The plane writes messages in lower case; the console shows sentences. */
export function sentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  const first = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?…)]$/.test(first) ? first : `${first}.`;
}

/* ------------------------------------------------- inline confirm */

/**
 * A destructive action that asks in place: the trigger turns into
 * "Revoke ci? [Revoke] [Cancel]". Never window.confirm.
 */
export function InlineConfirm({
  trigger,
  question,
  confirmLabel,
  onConfirm,
  busy = false,
  size = 'sm',
  triggerClass = 'btn btn--quiet',
  dismissLabel = 'Cancel',
}: {
  triggerClass?: string;
  /** The button that backs out; name it something else when the action itself is a cancel. */
  dismissLabel?: string;
  trigger: ReactNode;
  question: ReactNode;
  confirmLabel: string;
  onConfirm: () => void | Promise<unknown>;
  busy?: boolean;
  size?: 'sm' | '';
}) {
  const [open, setOpen] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) confirmRef.current?.focus();
    else if (wasOpen.current) triggerRef.current?.focus();
    wasOpen.current = open;
  }, [open]);
  const cls = size ? ` btn--${size}` : '';
  if (!open) {
    return (
      <button
        ref={triggerRef}
        type="button"
        className={`${triggerClass}${cls}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        {trigger}
      </button>
    );
  }
  return (
    <span
      className="confirm"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false);
      }}
      role="group"
      aria-label="Confirm"
    >
      <span className="confirm__q">{question}</span>
      <button
        ref={confirmRef}
        type="button"
        className={`btn btn--danger btn--primary${cls}`}
        aria-busy={busy}
        disabled={busy}
        onClick={async () => {
          await onConfirm();
          setOpen(false);
        }}
      >
        {busy ? <Spinner size={13} /> : null}
        {confirmLabel}
      </button>
      <button type="button" className={`btn btn--quiet${cls}`} onClick={() => setOpen(false)} disabled={busy}>
        {dismissLabel}
      </button>
    </span>
  );
}

/* ---------------------------------------------------- page chrome */

export function PageHead({ title, lede, actions, crumbs }: { title: ReactNode; lede?: ReactNode; actions?: ReactNode; crumbs?: ReactNode }) {
  return (
    <header className="head">
      <div className="head__text">
        {crumbs ? <nav className="crumbs" aria-label="Breadcrumb">{crumbs}</nav> : null}
        <h1>{title}</h1>
        {lede ? <p>{lede}</p> : null}
      </div>
      {actions ? <div className="head__actions">{actions}</div> : null}
    </header>
  );
}

export function Empty({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty__title">{title}</p>
      {children}
      {action}
    </div>
  );
}

export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={String(o.value)} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Placeholder rows while a table's first page loads. */
export function SkeletonRows({ cols, rows = 6 }: { cols: number; rows?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r} aria-hidden="true">
          {Array.from({ length: cols }, (_, c) => (
            <td key={c} className={c === 0 ? 'm-title' : c === cols - 1 ? 'm-end' : 'm-hide'}>
              <span className="skeleton" style={{ width: c === 0 ? 132 : 40 + ((r * 7 + c * 13) % 50) }} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
